import { EquipmentBillingTemplateDto } from './EquipmentBillingTemplateDto';

/**
 * One equipment bill line for the rate-sheet viewer (29990, un-grouped in 32009):
 * one row per equipment unit on the sheet, carrying its category, name (the unit
 * code), description (the EM master description), rate and unit of measure. WI 32009
 * un-grouped the earlier (category, frequency, rate) roll-up so Tim's "sort by
 * category then description" resolves per row and every unit's own rate stays
 * visible (honors 29990's "never collapse rates; show what the sheet shows").
 *
 * Extracted from operations-management's estimate-lookup.service to this shared
 * DTO lib for WI 32010 (import-paths-only; behavior unchanged) so Resource
 * Allocation can build its rate-sheet equipment picker from the same projection.
 */
export interface RateSheetEquipmentLine {
  category: string;
  /** The unit code from the sheet (EquipmentBillingTemplateDto.equipment, e.g. E420). */
  name: string;
  /** EM master description for the unit (equipmentDescription); blank when unmatched. */
  description: string;
  rate: number;
  /** Unit of measure / billing frequency from VP RevCode (Hrly/Shift/Daily/Wkly/Project). */
  uom: string;
}

/**
 * Fold a rate sheet's equipment bill rows (sparta_BillingEquipTemplate) into viewer
 * lines, one row per unit (29990, un-grouped in 32009). Keeps standard-rate rows
 * (RateOpt 'R'; blank counts as standard) with a positive rate. Does NOT exclude
 * restrictByEquip / restrictByRevCode rows: the real equipment rows are rev-code-keyed
 * and carry restrictByRevCode='Y', so excluding them dropped the entire sheet (the
 * 29990 first-cut bug). A row with no category of its own is promoted so the unit's
 * own label becomes the Category, and its name/description then stay blank rather than
 * repeat it. Sorted by category, then description, then name, then rate.
 */
export function mapEquipmentBillingToLines(
  rows: readonly EquipmentBillingTemplateDto[]
): RateSheetEquipmentLine[] {
  const lines: RateSheetEquipmentLine[] = [];
  for (const r of rows) {
    if (r.rate == null || r.rate <= 0) continue;
    if (r.rateOpt && r.rateOpt.trim().toUpperCase() !== 'R') continue;
    const unit = (r.equipment || '').trim();
    const description = (r.equipmentDescription ?? '').trim();
    const catLabel = (r.equipCategoryDesc || r.equipCategory || '').trim();
    // A row with no category of its own: the unit is the category, so it is not
    // repeated in the name/description columns (pre-32009 promotion behavior).
    const category = catLabel || description || unit;
    if (!category) continue;
    const hasOwnCategory = catLabel.length > 0;
    lines.push({
      category,
      name: hasOwnCategory ? unit : '',
      description: hasOwnCategory ? description : '',
      rate: r.rate,
      uom: (r.revCode || '').trim(),
    });
  }
  return lines.sort(
    (a, b) =>
      a.category.localeCompare(b.category) ||
      a.description.localeCompare(b.description) ||
      a.name.localeCompare(b.name) ||
      a.rate - b.rate
  );
}
